import type { Actor } from '@blueprint/domain'
import { create } from 'zustand'
import { createRepository, RepositoryError, type BoardRepository } from '../data/index.js'

/**
 * Who is signed in. Kept apart from the board store because it outlives every board: the
 * app asks this first, and only then decides whether to load a board at all.
 *
 * The browser never decides what a user may do — it decides what to show. Every answer
 * here came from the API, and every action is checked there again.
 */
export type SessionPhase = 'checking' | 'signed-out' | 'signed-in'

interface SessionStore {
  phase: SessionPhase
  user: Actor | null
  /** The tenant is on a Teamcentric Accounts trial: no data leaves it, so exports are hidden. */
  trial: boolean
  /** Set while an Accounts super admin is switched into this demo user (Switch as): where Back to Accounts goes. */
  backToAccountsUrl: string | null
  /** Set when the session could not be checked, or ended elsewhere. */
  error: string | null
  check: () => Promise<void>
  logOut: () => Promise<void>
  setUser: (user: Actor) => void
  /** Called when any request comes back 401: the session ended elsewhere. */
  sessionExpired: () => void
}

const repository: BoardRepository = createRepository()

export const useSessionStore = create<SessionStore>((set) => ({
  phase: 'checking',
  user: null,
  trial: false,
  backToAccountsUrl: null,
  error: null,

  async check() {
    try {
      const signedIn = await repository.currentUser()
      set(signedIn
        ? { phase: 'signed-in', user: signedIn.user, trial: signedIn.trial, backToAccountsUrl: signedIn.backToAccountsUrl, error: null }
        : { phase: 'signed-out', user: null })
    } catch (error) {
      // The API being unreachable is not the same as being signed out, but there is
      // nothing to show either way: the sign-in screen carries the message.
      set({
        phase: 'signed-out',
        user: null,
        error: error instanceof RepositoryError ? error.message : 'The server could not be reached.',
      })
    }
  },

  async logOut() {
    let logoutUrl: string | null = null
    try {
      logoutUrl = await repository.logOut()
    } finally {
      // Whatever the server said, this tab is done: never leave a half-signed-in screen.
      // On the way to Teamcentric Accounts the screen stays blank rather than starting a
      // new sign-in from the login page.
      set({ phase: logoutUrl ? 'checking' : 'signed-out', user: null, error: null })
    }
    // Central login: signed out of teamcentric too, at Accounts, which comes back to /login.
    if (logoutUrl) window.location.assign(logoutUrl)
  },

  setUser(user) {
    set({ user })
  },

  sessionExpired() {
    set({ phase: 'signed-out', user: null, error: 'Your session ended. Sign in again.' })
  },
}))

/** The repository the session store talks to, shared with the admin screens. */
export function sessionRepository(): BoardRepository {
  return repository
}
